/**
 * AppCheckService - Firebase App Check initialization (WI-012d)
 *
 * Initializes Firebase App Check with platform-specific attestation providers
 * to protect Firebase Auth phone verification against automated abuse:
 * - iOS: App Attest (iOS 14+) with DeviceCheck fallback
 *   (provider selection handled inside @capacitor-firebase/app-check)
 * - Android: Play Integrity
 * - Web: reCAPTCHA v3 via the Firebase JS SDK, when
 *   `environment.appCheck.webRecaptchaSiteKey` is configured; otherwise web
 *   App Check is skipped (see docs/security/app-check-setup.md - server-side
 *   enforcement must stay OFF until the web path is configured).
 *
 * **JS SDK bridge (native)**: The Capacitor plugin attests through the NATIVE
 * Firebase SDK, but this app performs Auth/Functions calls through the
 * Firebase JS SDK (via @angular/fire). Without a bridge, callable requests
 * would carry no App Check token and `context.app` would be undefined
 * server-side for every client. After native initialization, the native
 * token is therefore exposed to the JS SDK through a `CustomProvider` whose
 * `getToken` delegates to the plugin (pattern from the plugin's
 * firebase-js-sdk docs).
 *
 * **Initialization order**: Must run BEFORE any user-triggered Firebase Auth
 * calls (OTP send, callable functions) so those requests carry an App Check
 * token. Wired into the APP_INITIALIZER chain in app.config.ts ahead of
 * AuthService readiness.
 *
 * **Graceful degradation**: App Check failures must NEVER block the app.
 * A safety app that cannot start is worse than one without attestation, so
 * initialization errors are logged (devWarn) and swallowed. Server-side
 * enforcement (flag-gated, see functions/src/auth/rate-limiter.ts) is the
 * actual security boundary.
 *
 * **Debug provider**: In non-production builds the debug provider is used.
 * On native platforms the debug token is generated on-device and printed to
 * the native logs (adb logcat / Xcode console); on web the JS SDK prints it
 * to the browser console. Register tokens in Firebase Console -> App Check
 * -> Apps -> Manage debug tokens.
 * See docs/security/app-check-setup.md for the full checklist.
 *
 * @see specs_and_project_plan/workitems/phase-4/WI-012d-app-check-native-integration.md
 * @see docs/security/app-check-setup.md
 */

import { Injectable } from '@angular/core';
import { FirebaseAppCheck, GetTokenResult } from '@capacitor-firebase/app-check';
import { Capacitor } from '@capacitor/core';
// WI-012d boundary change (PM-authorized, see WI spec Section 14): the
// firebase/app-check + firebase/app imports are required to bridge the
// native App Check token into the Firebase JS SDK used by @angular/fire.
import { getApp } from 'firebase/app';
import {
  initializeAppCheck,
  AppCheckOptions,
  AppCheckToken,
  CustomProvider,
  ReCaptchaV3Provider,
} from 'firebase/app-check';
import { devLog, devWarn } from '@core';
import { environment } from '../../../environments/environment';

/**
 * Options passed to the native App Check plugin on initialization.
 */
interface AppCheckInitializeOptions {
  debugToken: boolean;
  isTokenAutoRefreshEnabled: boolean;
}

/**
 * Fallback TTL for bridged native tokens when the plugin does not report
 * an expiry (GetTokenResult.expireTimeMillis is optional). Firebase App
 * Check tokens have a 30-60 minute lifetime; 30 minutes is the safe floor.
 */
const DEFAULT_TOKEN_TTL_MS = 30 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppCheckService {
  private initialized = false;

  /**
   * Initialize App Check for the current platform.
   *
   * Call once during app startup, BEFORE user-triggered Firebase Auth
   * operations. Safe to call multiple times (subsequent calls are no-ops).
   * Never throws - failures degrade gracefully (see class docs).
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      devLog('[AppCheckService] Already initialized');
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        await this.initializeNative();
      } else {
        this.initializeWeb();
      }
    } catch (error) {
      // Graceful degradation (WI-012d completion criterion 6):
      // never crash or block the app when App Check init fails.
      devWarn(
        '[AppCheckService] Initialization failed - continuing without App Check',
        error
      );
    }
  }

  /**
   * Get the current App Check token for manual API calls (if needed).
   * The Firebase SDK attaches tokens automatically for most operations.
   *
   * @returns Token string, or null on web / when unavailable.
   */
  async getToken(): Promise<string | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    try {
      const result = await this.pluginGetToken();
      return result.token;
    } catch (error) {
      devWarn('[AppCheckService] Failed to get token', error);
      return null;
    }
  }

  /**
   * Check if native App Check attestation is supported on this platform.
   * (Web uses reCAPTCHA v3 via the JS SDK, not native attestation.)
   */
  isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Native (iOS/Android): initialize the Capacitor plugin (which registers
   * App Attest/DeviceCheck or Play Integrity, or the debug provider), then
   * bridge the native token into the Firebase JS SDK via a CustomProvider
   * so Auth/Functions requests made through @angular/fire carry the token.
   */
  private async initializeNative(): Promise<void> {
    const platform = Capacitor.getPlatform();
    const useDebugProvider = this.useDebugProvider();

    await this.pluginInitialize({
      // Debug provider for dev/emulator builds only. Real attestation
      // providers (App Attest/DeviceCheck on iOS, Play Integrity on
      // Android) are selected by the plugin when debugToken is false.
      debugToken: useDebugProvider,
      // Keep tokens fresh so auth calls always have a valid token.
      isTokenAutoRefreshEnabled: true,
    });

    // Bridge: expose the native token to the Firebase JS SDK.
    this.jsSdkInitialize({
      provider: new CustomProvider({
        getToken: async (): Promise<AppCheckToken> => {
          const result = await this.pluginGetToken();
          return {
            token: result.token,
            expireTimeMillis:
              result.expireTimeMillis ?? Date.now() + DEFAULT_TOKEN_TTL_MS,
          };
        },
      }),
      isTokenAutoRefreshEnabled: true,
    });

    this.initialized = true;
    devLog('[AppCheckService] Initialized (native + JS SDK bridge)', {
      platform,
      provider: useDebugProvider
        ? 'debug'
        : platform === 'ios'
          ? 'appAttest/deviceCheck'
          : 'playIntegrity',
    });
  }

  /**
   * Web: initialize App Check in the Firebase JS SDK with reCAPTCHA v3.
   *
   * Requires `environment.appCheck.webRecaptchaSiteKey` (created in Firebase
   * Console when registering the web app for App Check). When absent, web
   * App Check is skipped - in that state, server-side enforcement
   * (APP_CHECK_ENFORCE) MUST remain off or web sign-ins would be rejected.
   */
  private initializeWeb(): void {
    const siteKey = environment.appCheck?.webRecaptchaSiteKey;

    if (!siteKey) {
      devWarn(
        '[AppCheckService] Web App Check skipped - no reCAPTCHA v3 site key configured ' +
        '(environment.appCheck.webRecaptchaSiteKey). Server-side enforcement must stay OFF. ' +
        'See docs/security/app-check-setup.md'
      );
      return;
    }

    if (this.useDebugProvider()) {
      // JS SDK debug provider: token is printed to the browser console on
      // first load; register it in Firebase Console -> Manage debug tokens.
      (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN =
        true;
    }

    this.jsSdkInitialize({
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });

    this.initialized = true;
    devLog('[AppCheckService] Initialized (web)', {
      provider: this.useDebugProvider() ? 'debug' : 'reCaptchaV3',
    });
  }

  /**
   * Whether to use the App Check debug provider.
   *
   * Explicit environment configuration wins; otherwise any non-production
   * build uses the debug provider (real attestation requires store builds
   * and Firebase Console registration).
   */
  private useDebugProvider(): boolean {
    return environment.appCheck?.debugToken ?? !environment.production;
  }

  /**
   * Plugin / JS SDK call wrappers - separated so unit tests can spy on them.
   * Capacitor plugin proxies use dynamic method resolution and cannot be
   * spied on directly in Jasmine (same limitation as NotificationService),
   * and initializeAppCheck requires a configured FirebaseApp that is not
   * available in unit tests.
   */
  private pluginInitialize(options: AppCheckInitializeOptions): Promise<void> {
    return FirebaseAppCheck.initialize(options);
  }

  private pluginGetToken(): Promise<GetTokenResult> {
    return FirebaseAppCheck.getToken({ forceRefresh: false });
  }

  private jsSdkInitialize(options: AppCheckOptions): void {
    initializeAppCheck(getApp(), options);
  }
}
